import type {
  Policy,
  PolicyLimitation,
  PolicyInitiatorSetting,
  PolicyApproverSetting,
  PolicyChangeProposal,
  PolicyLimitationProposal,
  PolicyInitiatorSettingProposal,
  PolicyApproverProposal,
} from '@mls-wallet/shared-types';

/**
 * Convert PolicyLimitationProposal to PolicyLimitation | null
 * - null input = null (no limitation / don't change)
 * - { shouldRemove: true } = null (remove existing limitation)
 * - { shouldRemove: false, ... } = PolicyLimitation (set limitation)
 */
function toLimitation(
  proposal: PolicyLimitationProposal | null,
  existingLimitation?: PolicyLimitation | null
): PolicyLimitation | null {
  if (!proposal) {
    // null = don't change for UPDATE, or no limitation for CREATE
    return existingLimitation ?? null;
  }
  if (proposal.shouldRemove) {
    return null;
  }
  const { shouldRemove: _, ...limitation } = proposal;
  return limitation;
}

/**
 * Convert PolicyInitiatorSettingProposal to PolicyInitiatorSetting | null
 * - null input = null (no initiator filter / don't change)
 * - { shouldRemove: true } = null (remove existing initiator filter)
 * - { shouldRemove: false, type: 'user', userId } = { type: 'user', userId }
 * - { shouldRemove: false, type: 'group', groupId } = { type: 'group', groupId }
 */
function toInitiatorSetting(
  proposal: PolicyInitiatorSettingProposal | null | undefined,
  existingInitiatorSetting?: PolicyInitiatorSetting | null
): PolicyInitiatorSetting | null {
  if (!proposal) {
    // null = don't change for UPDATE, or no initiator filter for CREATE
    return existingInitiatorSetting ?? null;
  }
  if (proposal.shouldRemove) {
    return null;
  }
  // shouldRemove is false, extract the initiator setting fields
  if (proposal.type === 'user') {
    return {
      type: 'user',
      userId: proposal.userId,
      userName: proposal.userName,
    };
  } else {
    return {
      type: 'group',
      groupId: proposal.groupId,
      groupName: proposal.groupName,
      members: proposal.members,
    };
  }
}

/**
 * Convert PolicyApproverProposal to PolicyApproverSetting | null
 * - null input = null (no approver filter / don't change)
 * - { shouldRemove: true } = null (remove existing approver filter)
 * - { shouldRemove: false, type: 'user', userId } = { type: 'user', userId }
 * - { shouldRemove: false, type: 'group', groupId, threshold } = { type: 'group', groupId, threshold }
 */
function toApproverSetting(
  proposal: PolicyApproverProposal | null | undefined,
  existingApproverSetting?: PolicyApproverSetting | null
): PolicyApproverSetting | null {
  if (!proposal) {
    // null = don't change for UPDATE, or no approver filter for CREATE
    return existingApproverSetting ?? null;
  }
  if (proposal.shouldRemove) {
    return null;
  }
  // shouldRemove is false, extract the approver setting fields
  if (proposal.type === 'user') {
    return {
      type: 'user',
      userId: proposal.userId,
      userName: proposal.userName,
    };
  } else {
    return {
      type: 'group',
      groupId: proposal.groupId,
      groupName: proposal.groupName,
      threshold: proposal.threshold,
      members: proposal.members,
    };
  }
}

/**
 * Policy with change tracking for edit flows
 *
 * Extends Policy with a `change` field that tracks pending modifications.
 * Use `getPolicyState()` to derive the display state ('original' | 'new' | 'modified' | 'deleted').
 */
export type PolicyWithChange = Policy & {
  change?: PolicyChangeProposal | null; // null = no changes (original state)
};

// Type for account with minimal info needed to build Policy.accounts
type MinimalAccount = {
  id: string;
  name: string;
  address: string;
};

/**
 * Apply a policy change proposal to an existing policy (or create new).
 *
 * This function is the core utility for transforming policy change proposals into
 * their resulting Policy objects. It's used in two key scenarios:
 *
 * 1. **Edit Flow (EditPoliciesDialog)**:
 *    - User makes changes → PolicyChangeProposal objects are created
 *    - This function computes what the policies will look like for display
 *    - Enables "preview before submit" functionality
 *
 * 2. **Review Flow (ReviewPolicyChangesDialog)**:
 *    - Backend proposal is fetched (PolicyChangeProposal[] from API)
 *    - Convert backend proposals to PolicyChangeProposal format
 *    - This function shows what policies will look like after execution
 *    - Displays the "future state" for approval/rejection
 *
 * @param originalPolicy - The original policy (null for CREATE actions)
 * @param change - The change to apply (CREATE/UPDATE/DELETE)
 * @param availableAccounts - Optional list of all accounts for lookup (needed to build full account objects)
 * @returns The resulting policy after applying the change
 *
 * @example
 * // Edit flow: Show what a new policy will look like
 * const newPolicy = applyPolicyChange(null, {
 *   action: 'CREATE',
 *   policyName: 'My Policy',
 *   policyTransactionType: 'TOKEN_TRANSFER',
 *   // ...
 * }, allAccounts);
 *
 * @example
 * // Review flow: Show what an updated policy will look like after approval
 * const updatedPolicy = applyPolicyChange(existingPolicy, {
 *   action: 'UPDATE',
 *   policyId: existingPolicy.id,
 *   policyName: 'Updated Name',
 * }, allAccounts);
 */
export function applyPolicyChange(
  originalPolicy: Policy | null,
  change: PolicyChangeProposal,
  availableAccounts?: MinimalAccount[]
): Policy {
  if (change.action === 'CREATE') {
    if (
      !change.policyName ||
      !change.policyType ||
      !change.policyTransactionType ||
      !change.networkId
    ) {
      throw new Error('CREATE action requires all base fields');
    }

    // Build new policy from change
    // For CREATE, policyId is null from backend but we need an ID for frontend tracking
    const tempId = change.policyId || `temp-${crypto.randomUUID()}`;

    // Build accounts from policyAccountProposals
    const accounts =
      availableAccounts && change.policyAccountProposals
        ? change.policyAccountProposals
            .filter((ap) => ap.action === 'ADD')
            .map((ap) => {
              const fullAccount = availableAccounts.find(
                (acc) => acc.id === ap.multichainAccountId
              );
              if (!fullAccount) {
                return null;
              }
              return {
                id: fullAccount.id,
                name: fullAccount.name,
                address: fullAccount.address,
              };
            })
            .filter((a): a is NonNullable<typeof a> => a !== null)
        : [];

    const basePolicy = {
      id: tempId,
      onchainId: '', // Placeholder - real value assigned by zod object creation
      name: change.policyName,
      description: change.policyDescription || null,
      type: change.policyType,
      networkId: change.networkId,
      organizationId: originalPolicy?.organizationId || '',
      accounts,
      limitation:
        change.policyTransactionType === 'TOKEN_TRANSFER'
          ? toLimitation(change.limitation)
          : null,
      initiatorSetting: toInitiatorSetting(change.initiatorSetting),
      approverSetting: toApproverSetting(change.approverSetting),
      isDeleted: false,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    // Add transaction type specific fields (discriminated union)
    if (change.policyTransactionType === 'TOKEN_TRANSFER') {
      return {
        ...basePolicy,
        transactionType: 'TOKEN_TRANSFER',
        tokenTransferCondition: change.tokenTransferCondition || null,
      } satisfies Policy;
    } else if (change.policyTransactionType === 'CONTRACT_INTERACTION') {
      return {
        ...basePolicy,
        transactionType: 'CONTRACT_INTERACTION',
        contractInteractionCondition:
          change.contractInteractionCondition || null,
      } satisfies Policy;
    } else {
      return {
        ...basePolicy,
        transactionType: 'ANY',
      } satisfies Policy;
    }
  }

  if (change.action === 'UPDATE') {
    if (!originalPolicy) {
      throw new Error('UPDATE action requires an original policy');
    }

    // Build base fields (common to all policy types)
    const baseFields = {
      id: originalPolicy.id,
      onchainId: originalPolicy.onchainId, // Preserve existing onchainId
      name:
        change.policyName !== undefined && change.policyName !== null
          ? change.policyName
          : originalPolicy.name,
      description:
        change.policyDescription !== undefined
          ? change.policyDescription
          : originalPolicy.description,
      type:
        change.policyType !== undefined && change.policyType !== null
          ? change.policyType
          : originalPolicy.type,
      networkId:
        change.networkId !== undefined && change.networkId !== null
          ? change.networkId
          : originalPolicy.networkId,
      organizationId: originalPolicy.organizationId,
      limitation:
        (change.policyTransactionType ?? originalPolicy.transactionType) ===
        'TOKEN_TRANSFER'
          ? toLimitation(change.limitation, originalPolicy.limitation)
          : null,
      initiatorSetting: toInitiatorSetting(
        change.initiatorSetting,
        originalPolicy.initiatorSetting
      ),
      // Case: the change is an AUTO_APPROVAL or no change to approverSetting and the original
      // is AUTO_APPROVAL, then use null. Otherwise, use the approverSetting from the change
      approverSetting:
        (change.policyType ?? originalPolicy.type) === 'AUTO_APPROVAL'
          ? null
          : toApproverSetting(
              change.approverSetting,
              originalPolicy.approverSetting
            ),
      isDeleted: originalPolicy.isDeleted,
      createdAt: originalPolicy.createdAt,
      updatedAt: new Date(),
    };

    // Apply account changes
    let accounts = [...originalPolicy.accounts];
    const accountProposals =
      'policyAccountProposals' in change ? change.policyAccountProposals : [];
    if (accountProposals && accountProposals.length > 0) {
      accountProposals.forEach((accountChange) => {
        if (accountChange.action === 'REMOVE') {
          const index = accounts.findIndex(
            (a) => a.id === accountChange.multichainAccountId
          );
          if (index !== -1) {
            accounts.splice(index, 1);
          }
        } else if (accountChange.action === 'ADD' && availableAccounts) {
          // Only add if we have the full account data
          const fullAccount = availableAccounts.find(
            (acc) => acc.id === accountChange.multichainAccountId
          );
          if (fullAccount && !accounts.find((a) => a.id === fullAccount.id)) {
            accounts.push({
              id: fullAccount.id,
              name: fullAccount.name,
              address: fullAccount.address,
            });
          }
        }
      });
    }

    // Handle transaction type change (discriminated union)
    const newTransactionType =
      change.policyTransactionType !== undefined &&
      change.policyTransactionType !== null
        ? change.policyTransactionType
        : originalPolicy.transactionType;

    if (newTransactionType === 'TOKEN_TRANSFER') {
      return {
        ...baseFields,
        accounts,
        transactionType: 'TOKEN_TRANSFER',
        tokenTransferCondition:
          change.tokenTransferCondition !== undefined
            ? change.tokenTransferCondition
            : originalPolicy.transactionType === 'TOKEN_TRANSFER'
              ? originalPolicy.tokenTransferCondition
              : null,
      } satisfies Policy;
    } else if (newTransactionType === 'CONTRACT_INTERACTION') {
      return {
        ...baseFields,
        accounts,
        transactionType: 'CONTRACT_INTERACTION',
        contractInteractionCondition:
          change.contractInteractionCondition !== undefined
            ? change.contractInteractionCondition
            : originalPolicy.transactionType === 'CONTRACT_INTERACTION'
              ? originalPolicy.contractInteractionCondition
              : null,
      } satisfies Policy;
    } else {
      return {
        ...baseFields,
        accounts,
        transactionType: 'ANY',
      } satisfies Policy;
    }
  }

  if (change.action === 'DELETE') {
    if (!originalPolicy) {
      throw new Error('DELETE action requires an original policy');
    }
    return {
      ...originalPolicy,
      isDeleted: true,
    };
  }

  const _exhaustiveCheck: never = change;
  throw new Error(`Unknown action: ${_exhaustiveCheck}`);
}

/**
 * Compute the final state of all policies after applying a list of changes
 *
 * @param originalPolicies - List of original policies
 * @param changes - List of changes to apply
 * @param availableAccounts - Optional list of all accounts for lookup
 * @returns List of policies with their changes applied
 */
export function computeFinalPolicyState(
  originalPolicies: Policy[],
  changes: PolicyChangeProposal[],
  availableAccounts?: MinimalAccount[]
): PolicyWithChange[] {
  // Helper to add change tracking to a policy
  const withChange = (
    policy: Policy,
    change: PolicyChangeProposal | null
  ): PolicyWithChange => ({
    ...policy,
    change,
  });

  const policiesById = new Map<string, PolicyWithChange>(
    originalPolicies.map((p) => [p.id, withChange(p, null)])
  );

  const newPolicies: PolicyWithChange[] = [];

  // Apply each change
  for (const change of changes) {
    if (change.action === 'CREATE') {
      const newPolicy = applyPolicyChange(null, change, availableAccounts);
      newPolicies.push(withChange(newPolicy, change));
    } else if (change.action === 'UPDATE' && change.policyId) {
      const existing = policiesById.get(change.policyId);
      if (existing) {
        const updatedPolicy = applyPolicyChange(
          existing,
          change,
          availableAccounts
        );
        policiesById.set(change.policyId, withChange(updatedPolicy, change));
      }
    } else if (change.action === 'DELETE' && change.policyId) {
      const existing = policiesById.get(change.policyId);
      if (existing) {
        policiesById.set(change.policyId, withChange(existing, change));
      }
    }
  }

  // Add existing policies (original or modified) in original order first
  const result: PolicyWithChange[] = [];
  for (const policyWithChange of policiesById.values()) {
    result.push(policyWithChange);
  }

  result.push(...newPolicies);

  return result;
}

/**
 * Get the state badge for a policy based on its change
 */
export function getPolicyState(
  policyWithChange: PolicyWithChange
): 'original' | 'new' | 'modified' | 'deleted' {
  if (!policyWithChange.change) {
    return 'original';
  }

  if (policyWithChange.change.action === 'CREATE') {
    return 'new';
  }

  if (policyWithChange.change.action === 'UPDATE') {
    return 'modified';
  }

  if (policyWithChange.change.action === 'DELETE') {
    return 'deleted';
  }

  return 'original';
}
